import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Check, Quote, Star, Building2, Tag, Calendar } from "lucide-react";
import { PROJECTS } from "../lib/data";
import Breadcrumb from "../components/Breadcrumb";
import SectionLabel from "../components/SectionLabel";
import ScrollReveal from "../components/ScrollReveal";
import Lightbox from "../components/Lightbox";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.slug === slug);
  const [lbIdx, setLbIdx] = useState(null);

  if (!project) {
    return (
      <div className="page container-x" style={{ padding: "180px 0" }}>
        <h1>Project not found</h1>
        <button className="btn btn-primary" onClick={() => navigate("/work")} style={{ marginTop: 24 }}>Back to work</button>
      </div>
    );
  }

  const allImages = [project.cover, ...project.images];
  const idx = PROJECTS.findIndex((p) => p.slug === project.slug);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <div className="page" data-testid="project-detail-page">

      {/* ── 1. Full-bleed Banner ── */}
      <div className="pd-banner" onClick={() => setLbIdx(0)} data-testid="proj-hero-img">
        <img src={project.cover} alt={`${project.name} cover`} />
        <div className="pd-banner-overlay" />

        {/* Breadcrumb floating on top of banner */}
        <div className="pd-banner-breadcrumb">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: "Work", to: "/work" },
            { label: project.name, to: `/work/${project.slug}` },
          ]} />
        </div>

        {/* Tags floating bottom-left on banner */}
        <div className="pd-banner-tags">
          {project.tags.map((t) => (
            <span key={t} className="pill pill-dark">{t}</span>
          ))}
        </div>
      </div>

      {/* ── 2. Project Name + Meta Panel ── */}
      <section className="pd-title-panel">
        <div className="container-x">
          <div className="pd-title-inner">
            <div className="pd-title-left">
              <h1 className="pd-project-name">{project.name}.</h1>
              <p className="pd-short">{project.short}</p>
            </div>
            <div className="pd-meta-block">
              <div className="pd-meta-item">
                <span className="pd-meta-label"><Building2 size={12} /> Client</span>
                <span className="pd-meta-value">{project.client}</span>
              </div>
              <div className="pd-meta-item">
                <span className="pd-meta-label"><Tag size={12} /> Industry</span>
                <span className="pd-meta-value">{project.industry}</span>
              </div>
              <div className="pd-meta-item">
                <span className="pd-meta-label"><Calendar size={12} /> Year</span>
                <span className="pd-meta-value">{project.year}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Overview: Challenge & Solution ── */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={1}>Project Overview</SectionLabel>
          <h2 className="sec-title">The challenge & the solution.</h2>
          <div className="proj-overview-grid">
            <div className="card">
              <h4>// The Challenge</h4>
              <p>{project.challenge}</p>
            </div>
            <div className="card">
              <h4>// The Solution</h4>
              <p>{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Extra Visuals (project.images) ── */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={2}>Visuals</SectionLabel>
          <h2 className="sec-title">A look inside.</h2>
          <div className="proj-img-grid" style={{ marginTop: 32 }}>
            {project.images.map((img, i) => (
              <div
                key={img}
                className="pd-gallery-thumb"
                onClick={() => setLbIdx(i + 1)}
                data-testid={`proj-img-${i}`}
              >
                <img src={img} alt={`${project.name} ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Delivered ── */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={3}>Delivered</SectionLabel>
          <h2 className="sec-title">Everything shipped.</h2>
          <div className="card" style={{ marginTop: 32 }}>
            <ul className="deliv-list">
              {project.delivered.map((d) => (
                <li key={d}><Check size={18} /> {d}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 6. Results ── */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={4}>Results</SectionLabel>
          <h2 className="sec-title">Numbers that matter.</h2>
          <div className="results-grid">
            {project.results.map((r, i) => (
              <ScrollReveal key={r.lbl} delay={i * 80}>
                <div className="result-card">
                  <div className="num">{r.num}</div>
                  <div className="lbl">{r.lbl}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Testimonial ── */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={5}>Client Voice</SectionLabel>
          <div
            className="card pd-testimonial-card"
            data-testid="proj-testimonial"
          >
            <Quote size={36} style={{ color: "var(--carbon-black)", opacity: 0.4 }} />
            <p className="pd-testi-text">"{project.testimonial.text}"</p>
            <div className="pd-testi-footer">
              <div>
                <div className="pd-testi-name">{project.testimonial.name}</div>
                <div className="pd-testi-role">{project.testimonial.role}</div>
              </div>
              <div className="testi-stars">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="currentColor" />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Next project ── */}
      <section className="section section-bright" style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className="container-x">
          <Link to={`/work/${next.slug}`} className="next-card" data-testid="next-project">
            <div>
              <div className="lbl">// Next Project</div>
              <h3>{next.name}</h3>
            </div>
            <ArrowRight size={28} />
          </Link>
        </div>
      </section>

      {/* ── 9. CTA ── */}
      <div className="container-x">
        <div className="cta-dark">
          <div className="lead">Like what you see?</div>
          <h2>Let's work together on something just as good.</h2>
          <Link to="/contact" className="btn btn-light">Start a Project <ArrowRight size={16} /></Link>
        </div>
      </div>

      {/* Lightbox */}
      {lbIdx !== null && (
        <Lightbox
          images={allImages}
          index={lbIdx}
          onClose={() => setLbIdx(null)}
          onPrev={() => setLbIdx((lbIdx - 1 + allImages.length) % allImages.length)}
          onNext={() => setLbIdx((lbIdx + 1) % allImages.length)}
        />
      )}
    </div>
  );
}
